<template>
	<div class="class-filters">
		<BOffcanvas
			v-model="value"
			class="class-filters-offcanvas"
			:body-class="bodyClass"
			placement="end"
			@hide="close"
		>
			<template #header>
				<div class="class-filters-heading h6">
					<button
						v-if="selectedGroup"
						class="btn btn-simple btn-icon"
						data-testid="category-back-btn"
						@click="back"
					>
						<ArrowLeftParensIcon :height="16" :width="16"></ArrowLeftParensIcon>
						{{ selectedGroup.name }}
					</button>
					<button v-else class="btn btn-simple" disabled>Filters</button>
				</div>
				<button
					v-adobe-data-layer="closeADLConfig"
					class="btn-close align-self-start"
					data-testid="close-btn"
					aria-label="close filters button"
					@click="close"
				></button>
			</template>
			<div class="h-100 flex-column justify-content-between" :class="{ 'd-flex': !selectedGroup }">
				<div
					class="class-filters-menu"
					:class="{ open: selectedGroup }"
					:style="{ height: scrollHeight }"
				>
					<div class="class-filters-menu-level-1">
						<template v-for="group in groups" :key="group.name">
							<template v-if="hasFiltersForGroup(group)">
								<button
									class="class-filters-groups"
									:tabindex="selectedGroup?.open ? '-1' : '0'"
									data-testid="filter-category"
									@click="showSubcategories(group.name, $event)"
								>
									{{ group.name }}
									<span v-if="!groupFilters(group.id).length" class="class-filters-groups-selected">
										All {{ group.pluralName }}
									</span>
									<span
										v-else
										class="class-filters-groups-selected text-start text-truncate"
										style="width: 90%"
									>
										{{ groupFiltersString(group.id) }}
									</span>
								</button>

								<div class="class-filters-menu-level-2" :class="{ open: group.open }">
									<div v-for="category in group.categories" :key="category.id">
										<template v-if="hasFiltersForCategory(group, category)">
											<p class="caption mb-4">{{ category.name }}</p>
											<ul class="list-unstyled">
												<li
													v-for="filterName in getFilters(group, category)"
													:key="filterName"
													class="class-filters-check"
												>
													<div class="form-check">
														<input
															:id="filterName"
															class="form-check-input"
															type="checkbox"
															data-testid="filter-checkbox"
															:value="filterName"
															:checked="isChecked(group, filterName)"
															@change="onFilterChange(group, category, filterName, $event)"
														/>
														<label class="form-check-label small" :for="filterName">
															{{ filterName }}
														</label>
													</div>
												</li>
											</ul>
										</template>
									</div>
								</div>
							</template>
						</template>
					</div>
				</div>
				<div class="class-filters-ctas d-grid gap-2 py-5">
					<button
						v-adobe-data-layer="clearADLConfig"
						class="btn btn-simple btn-icon justify-content-center"
						:disabled="isClearButtonDisabled"
						data-testid="clear-btn"
						@click="onClearClick"
					>
						<MinusParensIcon></MinusParensIcon> Clear {{ openGroup ? '' : 'All' }}
					</button>
					<button
						v-adobe-data-layer="viewResultsADLConfig"
						class="btn btn-outline-dark"
						:class="{ active: selectedFilters.length > 0 }"
						data-testid="view-results-btn"
						@click="close"
					>
						View Results
					</button>
				</div>
			</div>
		</BOffcanvas>
	</div>
</template>

<script lang="ts" setup>
import { nextTick, ref, type Ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useVModel } from '@vueuse/core';
import { BOffcanvas } from 'bootstrap-vue-next';
import ArrowLeftParensIcon from '../icons/ArrowLeftParensIcon.vue';
import MinusParensIcon from '@/components/icons/MinusParensIcon.vue';
import { useScheduleStore } from '@/stores/schedule';
import vAdobeDataLayer from '@/directives/AdobeDataLayer';
import type {
	IFilterCategoryDef,
	IFilterGroupDef,
	IFilterSelection,
	InterestCategoryIds,
	IADLConfig
} from 'Types/aempsched/aempschedDay';

const props = defineProps<{
	modelValue: boolean;
}>();
const emit = defineEmits(['update:modelValue']);

const value = useVModel(props, 'modelValue', emit);

const scheduleStore = useScheduleStore();
const { filters, selectedFilters } = storeToRefs(scheduleStore);
const { getDataAgain, resetFilters } = scheduleStore;

const closeADLConfig: IADLConfig = {
	name: 'prospectschedules',
	type: 'button',
	title: 'Close'
};

const clearADLConfig: IADLConfig = {
	name: 'prospectschedules',
	type: 'button',
	title: 'Clear All'
};

const viewResultsADLConfig: IADLConfig = {
	name: 'prospectschedules',
	type: 'button',
	title: 'See Results'
};

const selectedGroup = ref<{
	name: string;
	open: boolean;
	categories: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
} | null>(null);

/** the filter group that is currently open, if any */
const openGroup = computed(() => groups.value.find((g) => g.open));

const isClearButtonDisabled = computed(() => {
	if (openGroup.value) {
		return !selectedFilters.value?.some((f) => f.group === openGroup.value?.id);
	}
	return !selectedFilters.value.length;
});

/** Helper function to parse out filter names such as `classes|Cardio` and return `Cardio` */
const parseCategoryFilter = (categoryFilter: string) => {
	const dividerIndex = categoryFilter.indexOf('|');
	return dividerIndex === -1 ? categoryFilter : categoryFilter.substring(dividerIndex + 1);
};

/** Returns true if there is a selected filter with this group and name */
const isChecked = (group: IFilterGroupDef, name: string) => {
	return selectedFilters.value.some(
		(f) => f.group === group.id && parseCategoryFilter(f.name) === name
	);
};

/** the selected filters for a particular filter group */
const groupFilters = (groupId: string) => {
	return selectedFilters.value.filter((f: IFilterSelection) => groupId === f.group);
};

/** Display string of selected filters for a particular filter group */
const groupFiltersString = (groupId: string) => {
	return groupFilters(groupId)
		.map((f) => parseCategoryFilter(f.name))
		.join(', ');
};

const hasFiltersForGroup = (group: IFilterGroupDef) => {
	if (group.id !== 'interests') {
		return Boolean(filters.value?.[group.id]?.length);
	}
	const interests = filters.value?.interests || {};
	return Object.keys(interests).some((interestCategory) => {
		const category = interestCategory as InterestCategoryIds;
		return Boolean(filters.value.interests?.[category]?.length);
	});
};

const hasFiltersForCategory = (group: IFilterGroupDef, category: IFilterCategoryDef) => {
	if (group.id !== 'interests') {
		return true; // this function is only for Interests
	}
	const interests = filters.value?.interests || {};
	const categoryId = category.id as InterestCategoryIds;
	return Boolean(interests?.[categoryId]?.length);
};

/** Get the array of filter names for the given group and category */
const getFilters = (group: IFilterGroupDef, category: IFilterCategoryDef): string[] => {
	if (group.id !== 'interests') return filters.value?.[group.id] || [];

	const categoryId = category.id as InterestCategoryIds;
	return filters.value?.interests?.[categoryId] || [];
};

const onFilterChange = (
	group: IFilterGroupDef,
	category: IFilterCategoryDef,
	filter: string,
	event: Event
) => {
	const el = event.target as HTMLInputElement;
	const { checked } = el;
	if (group.id === 'interests') {
		filter = `${category.id}|${filter}`;
	}

	// Replace the URL
	if (checked) {
		addQueryParam(group.id, filter);
	} else {
		removeQueryParam(group.id, filter);
	}
};

const addQueryParam = (key: string, addedValue: string) => {
	const url = new URL(window.location.href);
	const currentValues = url.searchParams.getAll(key);
	if (!currentValues.includes(addedValue) && addedValue) {
		url.searchParams.append(key, addedValue);
		changeUrl(url);
	}
};

const removeQueryParam = (key: string, removedValue: string) => {
	const url = new URL(window.location.href);
	const currentValues = url.searchParams.getAll(key);
	if (currentValues.includes(removedValue)) {
		// delete them all
		url.searchParams.delete(key);
		// add them back (except the removedValue)
		currentValues.forEach((curVal) => {
			if (curVal !== removedValue) {
				url.searchParams.append(key, curVal);
			}
		});
		changeUrl(url);
	}
};

const onClearClick = () => {
	if (openGroup.value) {
		clearFiltersForGroup(openGroup.value);
	} else {
		resetFilters();
	}
};

const clearFiltersForGroup = (group: IFilterGroupDef) => {
	const url = new URL(window.location.href);
	url.searchParams.delete(group.id);
	changeUrl(url);
};

const changeUrl = (url: URL) => {
	window.history.replaceState(null, '', url);
	getDataAgain();
};

const back = () => {
	selectedGroup.value = null;
	scrollHeight.value = 'auto';
	bodyClass.value = 'scroll';
	groups.value = groups.value.map((group) => {
		group.open = false;
		return group;
	});
};

const close = () => {
	value.value = false;
	back();
};

// Hard-coded structure used for a couple purposes:
// 1) keep track of the state of whether that filter group is currently `open` (visible in UI)
// 2) map names used in the API to labels to display in the UI:
//   - `interests` vs. `Interests`
//   - `sports` vs. `Sports`
//   - `timesOfDay` vs. `Time of Day` and `times` (when needed in plural)
//
// For (2), the ids and the structure of subcategory ids need to match what
// the API sends back. To keep TypeScript happy, type defs need to match too.
const groups = ref<IFilterGroupDef[]>([
	{
		id: 'interests',
		name: 'Interests',
		pluralName: 'interests',
		open: false,
		categories: [
			{ id: 'classes', name: 'Classes' },
			{ id: 'kids & Family', name: 'Kids & Family' },
			{ id: 'sports', name: 'Sports' }
		]
	},
	{
		id: 'timesOfDay',
		name: 'Time of Day',
		pluralName: 'times',
		open: false,
		categories: [{ id: 'times', name: '' }]
	},
	{
		id: 'ages',
		name: 'Age',
		pluralName: 'ages',
		open: false,
		categories: [{ id: 'ages', name: '' }]
	},
	{
		id: 'experienceLevels',
		name: 'Experience Level',
		pluralName: 'experience levels',
		open: false,
		categories: [{ id: 'levels', name: '' }]
	},
	{
		id: 'instructors',
		name: 'Instructor',
		pluralName: 'instructors',
		open: false,
		categories: [{ id: 'instructors', name: '' }]
	}
]);

const bodyClass: Ref<'scroll' | 'no-scroll'> = ref('scroll');
const scrollHeight = ref('auto');
// eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
const showSubcategories = async (groupName: Object, event: MouseEvent) => {
	const groupIndex = groups.value.findIndex((g) => g.name === groupName);
	groups.value[groupIndex].open = true;
	selectedGroup.value = groups.value[groupIndex];

	await nextTick();
	const target = event.currentTarget as HTMLElement;
	if (!target) return;
	const subMenu = target.nextElementSibling as HTMLElement;
	const offcanvasBody = target
		.closest('.offcanvas')
		?.querySelector('.offcanvas-body') as HTMLElement;
	if (subMenu.scrollHeight > offcanvasBody.offsetHeight) {
		scrollHeight.value = `${subMenu.scrollHeight}px`;
		bodyClass.value = 'scroll';
	} else {
		scrollHeight.value = `${offcanvasBody.clientHeight}px`;
		bodyClass.value = 'no-scroll';
	}
};
</script>

<style lang="scss" scoped>
$class-sched-border-color: rgba($border-color, 0.15);
.class-filters {
	&-heading {
		margin-bottom: map-get($spacers, 8);
	}

	&-menu {
		transition: transform 0.5s ease;

		&.open {
			transform: translateX(-100%);
		}

		&-level-1 {
			position: relative;
			width: 100%;
		}

		&-level-2 {
			position: absolute;
			top: 0;
			left: 100%;
			width: 100%;
			display: none;
			padding: 2rem 1.125rem;

			&.open {
				display: block;
			}

			ul {
				margin-bottom: map-get($spacers, 8);
			}
		}
	}

	&-groups {
		display: flex;
		width: 100%;
		flex-direction: column;
		align-items: flex-start;
		position: relative;
		padding: map-get($spacers, 3);
		background-color: transparent;
		border: none;
		border-bottom: solid 1px $class-sched-border-color;
		color: $charcoal;

		font-size: 1.0625rem;
		line-height: 1.5rem;

		&-selected {
			display: block;
			font-size: map-get($font-sizes, 'sm');
			line-height: 1rem;
			color: rgba($charcoal, 0.3);
		}

		&::after {
			content: '\f285';
			display: inline-flex;
			align-items: center;
			position: absolute;
			height: 100%;
			top: 0;
			right: map-get($spacers, 3);
			font-family: bootstrap-icons !important;
			-webkit-font-smoothing: antialiased;
			font-size: 0.75rem;
		}
	}

	&-check {
		padding: map-get($spacers, 2) 0;
		border-bottom: solid 1px $class-sched-border-color;
		.form-check-label {
			line-height: 1rem;
		}
	}

	&-ctas {
		position: sticky;
		bottom: 0;
		right: 1.5rem;
		padding: 0 var(--kpl-offcanvas-padding-x) var(--kpl-offcanvas-padding-y);
		background-color: var(--kpl-offcanvas-bg);
		border-radius: 0.5rem;
	}
}
</style>

<style lang="scss">
.class-filters-offcanvas {
	.offcanvas-body {
		padding: 0;
		overflow-x: hidden;

		&.scroll {
			overflow-y: auto;
		}

		&.no-scroll {
			overflow-y: hidden;
		}
	}
}
</style>
